import { DateFormatter, getMaxDateForAge } from '@repo/toolkit/date'
import { MIN_AGE_REQUIRED } from '@repo/toolkit/min-age-required'
import { z, ZodType } from 'zod'

import { GenderEnum } from '../../../../lib/types/enums/common/gender.enum'
import { IDTypeEnum } from '../../../../lib/types/enums/common/id-type.enum'
import { EnrollmentIdentityBase } from '../../../../lib/types/fields/enrollments/enrollment-identity'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import {
  EnrollmentHealth,
  EnrollmentHealthInput
} from '../../../../lib/types/models/enrollment/enrollment-health'
import { EnrollmentIdentityInput } from '../../../../lib/types/models/enrollment/enrollment-identity'
import {
  EnrollmentPickup,
  EnrollmentPickupInput
} from '../../../../lib/types/models/enrollment/enrollment-pickup'
import { NullableTransformer } from '../../../../lib/types/utils'
import { createDateStringSchema } from '../../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'

export function mapEnrollmentToEnrollmentPickupInput(
  enrollmentPickup: Pick<EnrollmentPickup, 'person' | 'firstName' | 'lastName' | 'phoneNumber'>
): EnrollmentPickupInput {
  return {
    person: enrollmentPickup.person,
    firstName: enrollmentPickup.firstName,
    lastName: enrollmentPickup.lastName,
    phoneNumber: enrollmentPickup.phoneNumber
  }
}

export function mapEnrollmentToEnrollmentIdentityInput(
  enrollment: Pick<
    Enrollment,
    | 'childIdentityPapersSubmitted'
    | 'childFirstName'
    | 'childLastName'
    | 'childBirthDate'
    | 'childGender'
    | 'childNationality'
    | 'residenceCountry'
  >,
  requiredDocuments: IDTypeEnum[],
  enrollmentIdentity?: EnrollmentIdentityBase
): EnrollmentIdentityInput {
  // check if the enrollment has a required document. if yes, set the first one as the identity document type
  const identityDocumentType = requiredDocuments?.[0]

  const result: EnrollmentIdentityInput = {
    childIdentityPapersSubmitted: enrollment.childIdentityPapersSubmitted,
    childFirstName: enrollment.childFirstName,
    childLastName: enrollment.childLastName,
    childBirthDate: enrollment.childBirthDate,
    childGender: enrollment.childGender,
    childNationality: enrollment.childNationality,
    residenceCountry: enrollment.residenceCountry,
    identityDocumentType: null,
    identityDocumentIssuingCountry: null,
    identityDocumentNumber: null,
    identityDocumentExpiration: null,
    identityDocumentIssuedAt: null
  }

  if (identityDocumentType) {
    result.identityDocumentType = enrollmentIdentity?.identityDocumentType ?? identityDocumentType
    result.identityDocumentIssuingCountry = enrollmentIdentity?.identityDocumentIssuingCountry
    result.identityDocumentNumber = enrollmentIdentity?.identityDocumentNumber
    result.identityDocumentExpiration = enrollmentIdentity?.identityDocumentExpiration
    result.identityDocumentIssuedAt = enrollmentIdentity?.identityDocumentIssuedAt
  }
  return result
}

const saveForLaterBaseEnrollmentIdentitySchema = z.object({
  // enrollment
  childIdentityPapersSubmitted: z.boolean().optional(),
  childFirstName: createStringSchema('Nom'),
  childLastName: createStringSchema('Prénom'),
  childBirthDate: createDateStringSchema({
    inputName: 'Date de naissance',
    maxDate: DateFormatter.toDateFormatted(getMaxDateForAge(MIN_AGE_REQUIRED))
  }),
  childGender: createEnumSchema({ fieldName: 'Sexe', enumType: GenderEnum }),
  childNationality: createStringSchema('Nationalité').nullish(),
  residenceCountry: createStringSchema('Pays de résidence').nullish(),

  // identity document
  identityDocumentNumber: createStringSchema('N° de passeport')
    .transform((value) => value?.toUpperCase() ?? null)
    .nullish(),
  identityDocumentIssuingCountry: createStringSchema('Pays émetteur').nullish(),
  identityDocumentExpiration: createDateStringSchema({
    inputName: "Date d'expiration"
  }).nullish(),
  identityDocumentIssuedAt: createDateStringSchema({ inputName: 'Date de délivrance' }).nullish(),
  identityDocumentType: createEnumSchema({
    fieldName: 'Document',
    enumType: IDTypeEnum
  }).nullish()
})

const submissionBaseEnrollmentIdentitySchema = z.object({
  // enrollment
  childIdentityPapersSubmitted: z.boolean(),
  childFirstName: createStringSchema('Nom'),
  childLastName: createStringSchema('Prénom'),
  childBirthDate: createDateStringSchema({
    inputName: 'Date de naissance',
    maxDate: DateFormatter.toDateFormatted(getMaxDateForAge(MIN_AGE_REQUIRED))
  }),
  childGender: createEnumSchema({ fieldName: 'Sexe', enumType: GenderEnum }),
  childNationality: createStringSchema('Nationalité'),
  residenceCountry: createStringSchema('Pays de résidence'),

  // identity document
  identityDocumentNumber: createStringSchema('N° de passeport')
    .transform((value) => value?.toUpperCase() ?? null)
    .nullish(),
  identityDocumentIssuingCountry: createStringSchema('Pays émetteur').nullish(),
  identityDocumentExpiration: createDateStringSchema({
    inputName: "Date d'expiration"
  }).nullish(),
  identityDocumentIssuedAt: createDateStringSchema({ inputName: 'Date de délivrance' }).nullish(),
  identityDocumentType: createEnumSchema({
    fieldName: 'Document',
    enumType: IDTypeEnum
  }).nullish()
})

const addIdentityDocumentSubmissionValidation = (
  values: EnrollmentIdentityInput,
  ctx: z.RefinementCtx
) => {
  if (!values.identityDocumentType) return

  if (!values.identityDocumentNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['identityDocumentNumber'],
      message:
        values.identityDocumentType === IDTypeEnum.PASSPORT
          ? 'N° de passeport requis'
          : 'N° Document requis'
    })
  }

  if (!values.identityDocumentIssuedAt) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['identityDocumentIssuedAt'],
      message: 'Date de délivrance requise'
    })
  }

  if (!values.identityDocumentExpiration) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['identityDocumentExpiration'],
      message: "Date d'expiration requise"
    })
  }

  if (!values.identityDocumentIssuingCountry) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['identityDocumentIssuingCountry'],
      message: 'Pays émetteur requis'
    })
  }
}

export const SAVE_FOR_LATER_ENROLLMENT_IDENTITY_FORM_SCHEMA_VALIDATION: ZodType<
  NullableTransformer<EnrollmentIdentityInput>
> = saveForLaterBaseEnrollmentIdentitySchema

export const SUBMISSION_ENROLLMENT_IDENTITY_FORM_SCHEMA_VALIDATION: ZodType<EnrollmentIdentityInput> =
  submissionBaseEnrollmentIdentitySchema.superRefine(addIdentityDocumentSubmissionValidation)

export function mapEnrollmentToEnrollmentHealthInput(
  enrollmentHealth?: Partial<
    Pick<
      EnrollmentHealth,
      | 'diphtheria'
      | 'tetanus'
      | 'poliomyelitis'
      | 'dtPolioTetracoq'
      | 'pertussis'
      | 'hepatitisB'
      | 'rubellaMumpsMeasles'
      | 'bcg'
      | 'haemophilus'
      | 'pneumococcus'
      | 'meningococcusC'
      | 'otherVaccinations'
      | 'additionalRecommendations'
    >
  > &
    Pick<Enrollment, 'medicalInformations' | 'additionalMedicalInformations'>
): EnrollmentHealthInput | undefined {
  if (!enrollmentHealth) return undefined

  const medicalRecommendations = [
    enrollmentHealth.medicalInformations?.trim(),
    enrollmentHealth.additionalMedicalInformations?.trim()
  ]
    .filter(Boolean)
    .join('\n\n')
    .trim()
  const additionalRecommendations = enrollmentHealth.additionalRecommendations?.trim()

  return {
    diphtheria: enrollmentHealth.diphtheria,
    tetanus: enrollmentHealth.tetanus,
    dtPolioTetracoq: enrollmentHealth.dtPolioTetracoq,
    pertussis: enrollmentHealth.pertussis,
    hepatitisB: enrollmentHealth.hepatitisB,
    otherVaccinations: enrollmentHealth.otherVaccinations,
    additionalRecommendations:
      (additionalRecommendations === '' ? undefined : additionalRecommendations) ??
      (medicalRecommendations === '' ? undefined : medicalRecommendations),
    bcg: enrollmentHealth.bcg,
    haemophilus: enrollmentHealth.haemophilus,
    meningococcusC: enrollmentHealth.meningococcusC,
    pneumococcus: enrollmentHealth.pneumococcus,
    poliomyelitis: enrollmentHealth.poliomyelitis,
    rubellaMumpsMeasles: enrollmentHealth.rubellaMumpsMeasles
  }
}
